import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  CircleMarker, MapContainer, Polyline, TileLayer, Tooltip as LTooltip, useMap,
} from "react-leaflet";
import type { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import { GitCompareArrows, ListChecks, Network, X } from "lucide-react";

import { SelectorNodosModal } from "@/components/SelectorNodosModal";
import {
  getMapaConectividad, getMapaDetalle, getMapaMarcadores, getMapaRed,
  type MapaConectividad, type MapaConectSub, type MapaDetalle, type MapaMarcador,
  type MapaUbicacion,
} from "@/lib/api-mercado";

/*
 * Mapa de precios del MEM: nodos (PML) o zonas de carga (PND), coloreados por
 * el precio promedio del último día publicado. Clic en un marcador → panel con
 * el promedio del último día, el promedio del año por hora y la serie de los últimos
 * 7 días disponibles (MDA vs MTR).
 *
 * Nota de precisión: CENACE no publica coordenadas de los NodosP. Cada nodo
 * trae su nivel de ubicación (`ubicacion`): la subestación real (OSM), la
 * localidad INEGI con su nombre, o la cabecera municipal como aproximación;
 * el panel lo dice y los aproximados llevan "≈" en el tooltip.
 *
 * Modo Operación (switch): sin nodo elegido (o en PND) se dibuja la red completa
 * de transmisión y subtransmisión (69–400 kV) de OpenStreetMap (GET /mapa/red);
 * con un nodo PML elegido se recorre desde su subestación
 * (GET /mapa/conectividad) y se dibujan
 * las líneas por tensión y las subestaciones alcanzadas (anillos) DEBAJO de los
 * marcadores, que conservan su color de precio; el panel lista por salto los
 * nodos de cada subestación con su precio y la diferencia frente al nodo elegido.
 */

// Etiqueta de cada nivel de ubicación (y si es una aproximación).
const UBICACION: Record<MapaUbicacion, { label: string; aprox: boolean }> = {
  subestacion: { label: "Subestación (OpenStreetMap)", aprox: false },
  localidad: { label: "Localidad INEGI", aprox: false },
  cabecera: { label: "Cabecera municipal", aprox: true },
  zona: { label: "Promedio de su zona de carga", aprox: true },
};
function ubicacionTexto(u?: MapaUbicacion, nombre?: string | null): string | null {
  if (!u) return null;
  const meta = UBICACION[u];
  const base = nombre ? `${meta.label} · ${nombre}` : meta.label;
  return meta.aprox ? `${base} (aprox.)` : base;
}

type Modo = "pml" | "pnd";
type Mercado = "mda" | "mtr";

// La comparación sobrevive recargas y navegaciones (mismo patrón que /tbfin y
// /matriz con localStorage, y que el selector de nodos del Portal).
const STORAGE_COMPARAR = "mapa.comparar";
function cargarComparar(): { modo: Modo; clave: string }[] {
  try {
    const raw = localStorage.getItem(STORAGE_COMPARAR);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr)
      ? arr.filter((x) => x && (x.modo === "pml" || x.modo === "pnd") && typeof x.clave === "string")
      : [];
  } catch {
    return [];
  }
}

const fmt = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmt0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });

// Modo Operación: el switch y sus casillas "Ver:" se recuerdan como el resto de
// preferencias del mapa.
const STORAGE_MODO_OP = "mapa.modoOperacion";
const STORAGE_VER_LINEAS = "mapa.operacion.verLineas";
const STORAGE_VER_SUBS = "mapa.operacion.verSubestaciones";
function cargarBool(key: string, porDefecto: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v == null ? porDefecto : v === "1";
  } catch {
    return porDefecto;
  }
}
function guardarBool(key: string, v: boolean): void {
  try {
    localStorage.setItem(key, v ? "1" : "0");
  } catch { /* sin almacenamiento */ }
}
// Colores de las líneas por tensión, distintos de la escala verde→rojo de
// precios y del azul de selección. La tensión de un enlace es la mayor de sus
// tramos (kv[0]); se agrupa en niveles para no tener siete colores.
const NIVELES_KV: { min: number; label: string; color: string }[] = [
  { min: 400, label: "400 kV", color: "#7c3aed" },
  { min: 230, label: "230 kV", color: "#0f766e" },
  { min: 138, label: "138 / 161 kV", color: "#2563eb" },
  { min: 115, label: "115 kV", color: "#0ea5e9" },
  { min: 0, label: "69 / 85 kV", color: "#64748b" },
];
const nivelKv = (kv: number[]) => NIVELES_KV.find((n) => (kv[0] ?? 0) >= n.min) ?? NIVELES_KV[NIVELES_KV.length - 1];
const colorKv = (kv: number[]) => nivelKv(kv).color;
const COLOR_SUB_ORIGEN = "#f59e0b";
const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtFecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]} ${y}`;
}

// Escala de color por cuantiles (verde barato → rojo caro), robusta a extremos.
const STOPS = ["#15803d", "#65a30d", "#eab308", "#f97316", "#dc2626"];
function makeEscala(precios: number[]) {
  const vals = [...precios].sort((a, b) => a - b);
  if (!vals.length) return { color: () => "#64748b", cortes: [] as number[], rango: () => -1 };
  const q = (p: number) => vals[Math.min(vals.length - 1, Math.floor(p * vals.length))];
  const cortes = [q(0.2), q(0.4), q(0.6), q(0.8)];
  // Rango (0 = más barato … 4 = más caro) al que cae un precio; -1 sin precio.
  const rango = (v: number | null) => {
    if (v == null) return -1;
    let i = 0;
    while (i < cortes.length && v > cortes[i]) i++;
    return i;
  };
  const color = (v: number | null) => (v == null ? "#94a3b8" : STOPS[rango(v)]);
  return { color, cortes, rango };
}

export function MapaPreciosPage() {
  const [modo, setModo] = useState<Modo>("pml");
  const [mercado, setMercado] = useState<Mercado>("mda");
  const [sel, setSel] = useState<string | null>(null);
  // Comparación: puede MEZCLAR nodos (PML) y zonas (PND) — cada elemento
  // recuerda su modo, y cambiar PML↔PND NO borra la selección.
  const [comparar, setComparar] = useState<{ modo: Modo; clave: string }[]>(cargarComparar);
  const [verComparacion, setVerComparacion] = useState(false);
  // "Seleccionar nodos…": elegir de una lista con búsqueda en vez de ir
  // clicando marcadores uno por uno.
  const [selectorAbierto, setSelectorAbierto] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COMPARAR, JSON.stringify(comparar));
    } catch { /* sin almacenamiento (modo privado): la selección vive solo en memoria */ }
  }, [comparar]);
  // Modo Operación: con un nodo elegido se recorre la red 400/230 kV (OSM)
  // desde su subestación y se dibuja en el mapa (ver PanelConectividad).
  const [modoOperacion, setModoOperacion] = useState<boolean>(() => cargarBool(STORAGE_MODO_OP, false));
  const [saltos, setSaltos] = useState(2);
  // "Ver:" qué capas de la red se dibujan (las dos prendidas por defecto).
  const [verLineas, setVerLineas] = useState<boolean>(() => cargarBool(STORAGE_VER_LINEAS, true));
  const [verSubs, setVerSubs] = useState<boolean>(() => cargarBool(STORAGE_VER_SUBS, true));
  useEffect(() => guardarBool(STORAGE_MODO_OP, modoOperacion), [modoOperacion]);
  useEffect(() => guardarBool(STORAGE_VER_LINEAS, verLineas), [verLineas]);
  useEffect(() => guardarBool(STORAGE_VER_SUBS, verSubs), [verSubs]);
  const enComparacion = (m: Modo, clave: string) =>
    comparar.some((x) => x.modo === m && x.clave === clave);

  const marcadoresQ = useQuery({
    queryKey: ["mapa", modo, mercado],
    queryFn: () => getMapaMarcadores(modo, mercado),
    staleTime: 5 * 60 * 1000,
  });

  const detalleQ = useQuery({
    queryKey: ["mapa-detalle", modo, sel],
    queryFn: () => getMapaDetalle(modo, sel!),
    enabled: sel != null,
    staleTime: 5 * 60 * 1000,
  });

  // Recorrido de la red desde el nodo elegido (solo PML y con el switch prendido).
  const conectQ = useQuery({
    queryKey: ["mapa-conectividad", sel, saltos],
    queryFn: () => getMapaConectividad(sel!, saltos),
    enabled: modoOperacion && modo === "pml" && sel != null,
    staleTime: 10 * 60 * 1000,
  });
  const red = modoOperacion && modo === "pml" && sel != null ? conectQ.data : undefined;
  const redActiva = !!red?.subestacion;
  // Sin nodo elegido —o en PND, donde no hay nodo desde el que recorrer— las
  // casillas "Ver:" muestran la red COMPLETA del extracto (estática: el
  // cliente la guarda una hora).
  const verTodo = modoOperacion && (modo === "pnd" || sel == null);
  // Ir a un nodo desde la red completa: en PND primero se cambia a PML (la
  // selección de PND es una zona, no un nodo).
  const irANodo = (clave: string) => {
    setModo("pml");
    setSel(clave);
  };
  const redTodaQ = useQuery({
    queryKey: ["mapa-red"],
    queryFn: getMapaRed,
    enabled: verTodo && (verLineas || verSubs),
    staleTime: 60 * 60 * 1000,
  });
  const redToda = verTodo ? redTodaQ.data : undefined;
  // Una polilínea múltiple por nivel de tensión: 8 mil enlaces como capas
  // sueltas con tooltip serían lentos; así son cinco capas.
  const porNivel = useMemo(() => {
    const m = new Map<(typeof NIVELES_KV)[number], [number, number][][]>();
    for (const e of redToda?.enlaces ?? []) {
      const n = nivelKv(e.kv);
      const arr = m.get(n) ?? [];
      for (const linea of e.geom) arr.push(linea);
      m.set(n, arr);
    }
    return m;
  }, [redToda]);
  // Enlaces que alimentan la leyenda (los del nodo, o los de la red completa).
  const enlacesLeyenda = redActiva && red ? red.enlaces : redToda ? redToda.enlaces : null;
  const boundsRed = useMemo<LatLngBoundsExpression | null>(() => {
    if (!red?.subestacion) return null;
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    const agrega = (lat: number, lon: number) => {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    };
    for (const s of red.subestaciones) agrega(s.lat, s.lon);
    for (const e of red.enlaces) for (const linea of e.geom) for (const p of linea) agrega(p[0], p[1]);
    return minLat <= maxLat ? [[minLat, minLon], [maxLat, maxLon]] : null;
  }, [red]);

  const items = marcadoresQ.data?.items ?? [];
  // Precio (promedio del último día del mercado elegido) por clave, para el
  // panel de conectividad.
  const precioDe = useMemo(
    () => new Map<string, number | null>(items.map((i) => [i.clave, i.precio])),
    [items],
  );
  const escala = useMemo(
    () => makeEscala(items.map((i) => i.precio).filter((p): p is number => p != null)),
    [items],
  );
  // Los círculos de la escala también FILTRAN: clic = ocultar/mostrar ese rango
  // de precio (como los chips de zona en /compras-mda). Los cortes no cambian
  // al filtrar: se calculan sobre todos los marcadores del modo.
  const [rangosOff, setRangosOff] = useState<Set<number>>(new Set());
  const toggleRango = (i: number) =>
    setRangosOff((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  // "Solo seleccionados": el mapa se queda con los nodos/zonas de la selección
  // (del modo actual). Se prende solo al aplicar desde "Seleccionar nodos…" —
  // eso es lo que uno espera al elegir de una lista— y se puede apagar para
  // seguir agregando puntos desde el mapa.
  const [soloSel, setSoloSel] = useState(false);
  const filtrando = rangosOff.size > 0 || (soloSel && comparar.length > 0);
  const visibles = useMemo(
    () => (!filtrando
      ? items
      : items.filter((it) =>
          (it.precio == null || !rangosOff.has(escala.rango(it.precio)))
          && (!soloSel || comparar.length === 0 || enComparacion(modo, it.clave)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, rangosOff, escala, soloSel, comparar, modo, filtrando],
  );
  const quitarFiltros = () => {
    setRangosOff(new Set());
    setSoloSel(false);
  };

  const cambiarModo = (m: Modo) => {
    setModo(m);
    setSel(null);
  };
  const toggleComparar = (m: Modo, clave: string) =>
    setComparar((cs) =>
      cs.some((x) => x.modo === m && x.clave === clave)
        ? cs.filter((x) => !(x.modo === m && x.clave === clave))
        : [...cs, { modo: m, clave }]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Mapa de Precios</h1>
          <p className="text-sm text-slate-500 mt-1">
            {modo === "pml" ? "Precios Marginales Locales por NodoP" : "Precios Nodales Distribuidos por Zona de Carga"}
            {" · color = promedio del "}
            <span className="font-medium">{marcadoresQ.data?.fecha ? fmtFecha(marcadoresQ.data.fecha) : "…"}</span>
            {" ("}{mercado.toUpperCase()}{") · $/MWh"}
          </p>
        </div>
        <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-white">
          {(["pml", "pnd"] as const).map((m) => (
            <button key={m} type="button" onClick={() => cambiarModo(m)}
              className={`px-3 py-1.5 rounded-md text-sm ${modo === m ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {m === "pml" ? "PML · Nodos" : "PND · Zonas"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-white">
          {(["mda", "mtr"] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMercado(m)}
              className={`px-3 py-1.5 rounded-md text-sm ${mercado === m ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {m.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSelectorAbierto(true)}
          className="inline-flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm bg-white text-slate-700 hover:bg-slate-50"
          title="Elegir nodos y zonas de una lista con búsqueda para compararlos"
        >
          <ListChecks className="w-4 h-4" /> Seleccionar nodos…
        </button>
        <button
          type="button"
          onClick={() => setModoOperacion((v) => !v)}
          aria-pressed={modoOperacion}
          title="Modo Operación: al elegir un nodo se resaltan las líneas (69 a 400 kV, OpenStreetMap) que salen de su subestación, las subestaciones que alcanza y nuestros nodos en ellas con su precio"
          className={`inline-flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm ${
            modoOperacion
              ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
              : "bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          <Network className="w-4 h-4" /> Modo Operación
        </button>
        {modoOperacion && (
          <div className="flex items-center gap-3 text-xs text-slate-600 border rounded-lg px-3 py-1.5 bg-white">
            <span className="font-medium text-slate-700">Ver:</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer"
              title="Dibujar las líneas (69 a 400 kV, según OpenStreetMap) que salen de la subestación del nodo">
              <input type="checkbox" checked={verLineas} onChange={(e) => setVerLineas(e.target.checked)}
                className="accent-amber-500" />
              Líneas de distribución
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer"
              title="Dibujar los anillos de las subestaciones alcanzadas (clic en uno recorre la red desde ahí)">
              <input type="checkbox" checked={verSubs} onChange={(e) => setVerSubs(e.target.checked)}
                className="accent-amber-500" />
              Subestaciones
            </label>
          </div>
        )}
        {modoOperacion && modo === "pnd" && (
          <span className="text-[11px] text-amber-700">
            {verLineas || verSubs ? "Mostrando toda la red · " : ""}para recorrerla desde un nodo cambia a PML · Nodos
            (o haz clic en una subestación).
          </span>
        )}
        {modoOperacion && modo === "pml" && sel == null && (
          <span className="text-[11px] text-amber-700">
            {verLineas || verSubs ? "Mostrando toda la red · " : ""}clic en un nodo para ver solo la suya.
          </span>
        )}
        {escala.cortes.length > 0 && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span className="font-medium text-slate-600 mr-0.5" title="Clic en un rango para ocultarlo o mostrarlo">
              Escala:
            </span>
            {STOPS.map((c, i) => {
              const off = rangosOff.has(i);
              const etiqueta = i < escala.cortes.length
                ? `≤ ${fmt0.format(escala.cortes[i])}`
                : `> ${fmt0.format(escala.cortes[escala.cortes.length - 1])}`;
              return (
                <button key={c} type="button" onClick={() => toggleRango(i)}
                  title={off ? "Rango oculto — clic para mostrarlo" : "Clic para ocultar este rango"}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition ${
                    off ? "bg-white text-slate-400 line-through" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}>
                  <span className="w-3 h-3 rounded-full inline-block"
                    style={{ background: c, opacity: off ? 0.3 : 1 }} />
                  {etiqueta}
                </button>
              );
            })}
            {filtrando && (
              <button type="button" onClick={quitarFiltros}
                className="ml-1 text-blue-600 hover:underline"
                title="Volver a mostrar todos los puntos (quita el filtro de rangos y el de seleccionados)">
                mostrando {visibles.length} de {items.length} · quitar filtros
              </button>
            )}
          </div>
        )}
        {comparar.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-slate-700">
              {comparar.length} en comparación
              <span className="text-slate-400 font-normal">
                {" "}({comparar.filter((x) => x.modo === "pml").length} PML ·{" "}
                {comparar.filter((x) => x.modo === "pnd").length} PND)
              </span>
            </span>
            <button
              type="button"
              onClick={() => setSoloSel((v) => !v)}
              aria-pressed={soloSel}
              title={soloSel
                ? "El mapa muestra solo los seleccionados — clic para ver todos (y poder agregar más desde el mapa)"
                : "Dejar en el mapa solo los seleccionados"}
              className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 ${
                soloSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <ListChecks className="w-3.5 h-3.5" /> Solo seleccionados
            </button>
            <button
              type="button"
              onClick={() => setVerComparacion(true)}
              disabled={comparar.length < 2}
              title={comparar.length < 2 ? "Agrega al menos 2 para comparar" : undefined}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded px-2.5 py-1.5 hover:bg-slate-700 disabled:opacity-50"
            >
              <GitCompareArrows className="w-3.5 h-3.5" /> Mostrar comparación
            </button>
            <button
              type="button"
              onClick={() => { setComparar([]); setSoloSel(false); }}
              className="text-slate-400 hover:text-slate-700"
              title="Vaciar la comparación"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-3" style={{ height: "calc(100vh - 210px)", minHeight: 480 }}>
        {/* z-0 aísla los z-index internos de Leaflet (400–1000) en su propio
            stacking context; sin esto tapan el dropdown del menú (z-20). */}
        <div className="flex-1 border rounded-lg overflow-hidden bg-white relative z-0">
          {marcadoresQ.isLoading && (
            <div className="absolute inset-0 z-[500] grid place-items-center bg-white/70 text-sm text-slate-500">
              Cargando {modo === "pml" ? "nodos" : "zonas"}…
            </div>
          )}
          <MapContainer center={[23.8, -102.2]} zoom={5} preferCanvas
            style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* La red del Modo Operación va ANTES que los marcadores: en el canvas
                se pinta en orden, así los nodos quedan encima de las líneas y de
                los anillos de subestación y conservan su color de precio. */}
            {redToda && (
              <>
                {verLineas && [...porNivel.entries()].map(([n, pos]) => (
                  <Polyline
                    key={`toda-${n.min}`}
                    positions={pos}
                    pathOptions={{ color: n.color, weight: n.min >= 400 ? 2.5 : n.min >= 230 ? 2 : 1.2, opacity: 0.75 }}
                  />
                ))}
                {verSubs && redToda.subestaciones.map((s) => (
                  <CircleMarker
                    key={`toda-${s.id}`}
                    center={[s.lat, s.lon]}
                    radius={4}
                    pathOptions={{ color: "#0f172a", weight: 1.5, fillColor: "#ffffff", fillOpacity: 0.6 }}
                    eventHandlers={{ click: () => { if (s.nodos[0]) irANodo(s.nodos[0]); } }}
                  >
                    <LTooltip direction="top" offset={[0, -4]}>
                      <span className="text-xs">
                        <b>{s.nombre ?? "Subestación sin nombre"}</b>
                        {s.kv.length > 0 && ` · ${s.kv.map((k) => `${k} kV`).join("/")}`}<br />
                        {s.nodos.length > 0
                          ? `${s.nodos.length} ${plural(s.nodos.length, "nodo", "nodos")}: ${s.nodos.slice(0, 4).join(", ")}${s.nodos.length > 4 ? "…" : ""} · clic para recorrer${modo === "pnd" ? " (pasa a PML)" : ""}`
                          : "sin nodos de precios anclados"}
                      </span>
                    </LTooltip>
                  </CircleMarker>
                ))}
              </>
            )}
            {redActiva && red && (
              <>
                {/* Enlaces: color por tensión; punteado = la línea sigue hacia una
                    subestación que OSM no tiene mapeada. */}
                {verLineas && red.enlaces.map((e, i) => (
                  <Polyline
                    key={`${e.a}-${e.b ?? "fin"}-${i}`}
                    positions={e.geom}
                    pathOptions={{
                      color: colorKv(e.kv), weight: e.salto === 1 ? 4 : 2.5, opacity: 0.9,
                      dashArray: e.b ? undefined : "6 6",
                    }}
                  >
                    <LTooltip sticky>
                      <span className="text-xs">
                        <b>{e.nombre ?? "Línea sin nombre en OSM"}</b>
                        {" · "}{e.kv.map((k) => `${k} kV`).join(" / ")}<br />
                        {nombreSub(red, e.a)} → {e.b ? nombreSub(red, e.b) : "subestación no mapeada"}
                        {" · ~"}{fmt0.format(e.km)} km
                      </span>
                    </LTooltip>
                  </Polyline>
                ))}
                {/* Subestaciones como ANILLOS (relleno casi transparente): el nodo
                    que vive dentro sigue mostrando su color de precio. */}
                {verSubs && red.subestaciones.map((s) => (
                  <CircleMarker
                    key={s.id}
                    center={[s.lat, s.lon]}
                    radius={s.salto === 0 ? 13 : 10}
                    pathOptions={{
                      color: s.salto === 0 ? COLOR_SUB_ORIGEN : "#0f172a", weight: s.salto === 0 ? 3.5 : 2.5,
                      fillColor: "#ffffff", fillOpacity: 0.15,
                    }}
                    eventHandlers={{
                      click: () => {
                        const n = s.nodos[0];
                        if (n && n.clave !== sel) setSel(n.clave);
                      },
                    }}
                  >
                    <LTooltip direction="top" offset={[0, -6]}>
                      <span className="text-xs">
                        <b>{s.nombre ?? "Subestación sin nombre"}</b>
                        {s.kv.length > 0 && ` · ${s.kv.map((k) => `${k} kV`).join("/")}`}<br />
                        {s.salto === 0 ? "subestación del nodo elegido" : `salto ${s.salto} · ~${fmt0.format(s.km ?? 0)} km`}
                        {s.nodos.length > 0
                          ? ` · ${s.nodos.length} ${plural(s.nodos.length, "nodo", "nodos")}`
                          : " · sin nodos anclados"}
                        {s.nodos.length > 0 && s.nodos[0].clave !== sel && <><br />clic para recorrer desde aquí</>}
                      </span>
                    </LTooltip>
                  </CircleMarker>
                ))}
                <AjustarVista bounds={boundsRed} clave={`${sel}|${saltos}`} />
              </>
            )}
            {visibles.map((it) => (
              <Marcador key={it.clave} it={it} modo={modo} color={escala.color(it.precio)}
                sel={sel === it.clave} enComp={enComparacion(modo, it.clave)}
                onClick={() => setSel(it.clave)} />
            ))}
          </MapContainer>
          {verTodo && redTodaQ.isLoading && (
            <div className="absolute inset-x-0 top-2 z-[500] text-center pointer-events-none">
              <span className="bg-white/90 border rounded px-2 py-1 text-xs text-slate-600 shadow">Cargando toda la red…</span>
            </div>
          )}
          {enlacesLeyenda && (
            <div className="absolute bottom-3 left-3 z-[500] bg-white/95 border rounded-md shadow px-2.5 py-1.5 text-[11px] text-slate-600 space-y-0.5">
              <div className="font-medium text-slate-700">
                Modo Operación · {redActiva ? "red del nodo elegido" : "red completa"} (OSM)
              </div>
              {verLineas && (
                <>
                  {/* Solo los niveles de tensión presentes en lo dibujado. */}
                  {NIVELES_KV.filter((n) => enlacesLeyenda.some((e) => nivelKv(e.kv) === n)).map((n) => (
                    <div key={n.min} className="flex items-center gap-1.5">
                      <span className="inline-block w-5 h-1 rounded" style={{ background: n.color }} /> {n.label}
                    </div>
                  ))}
                  {redActiva && (
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-5 border-t-2 border-dashed border-slate-500" /> sigue sin subestación mapeada
                    </div>
                  )}
                </>
              )}
              {verSubs && redActiva && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-[3px]" style={{ borderColor: COLOR_SUB_ORIGEN }} />
                  subestación de origen ·
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-slate-900" /> alcanzada
                </div>
              )}
              {verSubs && !redActiva && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-slate-900 bg-white" />
                  subestación · clic = recorrer desde su nodo
                </div>
              )}
              {!verLineas && !verSubs && (
                <div className="text-slate-500">Red oculta: activa «Ver: líneas» o «subestaciones».</div>
              )}
              <div className="text-slate-500">Los nodos conservan su color de precio.</div>
            </div>
          )}
        </div>

        {sel != null && (
          <div className="w-[400px] shrink-0 border rounded-lg bg-white overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">
                  {detalleQ.data?.nombre ?? sel}
                  <span className="ml-2 font-mono text-xs text-slate-400">{sel}</span>
                </div>
                {detalleQ.data && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {detalleQ.data.sistema}
                    {detalleQ.data.tipo === "pml" && <> · zona {detalleQ.data.zona}</>}
                    {detalleQ.data.tipo === "pnd" && detalleQ.data.n_nodos != null && <> · {detalleQ.data.n_nodos} nodos</>}
                    {detalleQ.data.municipio && <> · {detalleQ.data.municipio}</>}
                    {detalleQ.data.entidad && <>, {detalleQ.data.entidad}</>}
                    {detalleQ.data.tension_kv != null && <> · {fmt0.format(detalleQ.data.tension_kv)} kV</>}
                  </div>
                )}
                {detalleQ.data?.tipo === "pml" && detalleQ.data.ubicacion && (
                  <div className={`text-[11px] mt-0.5 ${
                    UBICACION[detalleQ.data.ubicacion].aprox ? "text-amber-700" : "text-slate-500"
                  }`}>
                    Ubicación: {ubicacionTexto(detalleQ.data.ubicacion, detalleQ.data.ubicacion_nombre)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => toggleComparar(modo, sel)}
                  title={enComparacion(modo, sel)
                    ? "Quitar de la comparación"
                    : "Agregar a la comparación (arriba aparece el contador y el botón Mostrar comparación; se pueden mezclar PML y PND)"}
                  className={`inline-flex items-center gap-1 text-xs rounded px-2 py-1 border ${
                    enComparacion(modo, sel)
                      ? "bg-slate-900 text-white border-slate-900"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <GitCompareArrows className="w-3.5 h-3.5" />
                  {enComparacion(modo, sel) ? "En comparación ✓" : "Comparar"}
                </button>
                <button type="button" onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {modoOperacion && modo === "pml" && (
              <PanelConectividad
                q={conectQ}
                saltos={saltos}
                onSaltos={setSaltos}
                sel={sel}
                precioDe={precioDe}
                mercado={mercado}
                onIr={(clave) => setSel(clave)}
              />
            )}

            {detalleQ.isLoading && <div className="px-4 py-8 text-center text-sm text-slate-500">Cargando…</div>}
            {detalleQ.isError && (
              <div className="px-4 py-8 text-center text-sm text-red-600">{(detalleQ.error as Error).message}</div>
            )}

            {detalleQ.data && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {(["mda", "mtr"] as const).map((m) => {
                    const s = detalleQ.data.mercados[m];
                    return (
                      <div key={m} className="border rounded-lg p-3">
                        <div className="text-[11px] font-medium text-slate-500">{m.toUpperCase()} · promedio último día</div>
                        <div className="text-lg font-semibold text-slate-900">
                          {s.ultimo?.precio != null ? `$${fmt.format(s.ultimo.precio)}` : "—"}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {s.ultimo ? fmtFecha(s.ultimo.fecha) : "sin datos"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1.5">
                          Promedio {detalleQ.data.anio ?? ""}:{" "}
                          <span className="font-medium text-slate-700">
                            {s.promedio_anio != null ? `$${fmt.format(s.promedio_anio)}` : "—"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Grafica titulo={`Promedio ${detalleQ.data.anio ?? "del año"} por hora del día`}
                  data={detalleQ.data.perfil_hora} xKey="hora" xInterval={3} />
                <Grafica titulo="Últimos días disponibles (por hora) — MDA hasta hoy, MTR con rezago"
                  data={detalleQ.data.serie7d.map((p) => ({ ...p, x: `${p.fecha.slice(8, 10)}/${p.fecha.slice(5, 7)} h${p.hora}` }))}
                  xKey="x" xInterval={23} />

                <p className="text-[10px] text-slate-400">
                  MDA = Mercado del Día en Adelanto · MTR = Tiempo Real (se publica con rezago).
                  El catálogo CENACE no publica coordenadas: los nodos se ubican en su subestación
                  (OpenStreetMap) o en la localidad INEGI que lleva su nombre; si no se encontró
                  ninguna, en la cabecera de su municipio (aprox.).
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <SelectorNodosModal
        open={selectorAbierto}
        mercado={mercado}
        inicial={comparar}
        onClose={() => setSelectorAbierto(false)}
        onApply={(sel) => { setComparar(sel); setSoloSel(sel.length > 0); }}
      />

      {verComparacion && comparar.length >= 2 && (
        <ComparacionModal
          items={comparar}
          onQuitar={toggleComparar}
          onClose={() => setVerComparacion(false)}
        />
      )}
    </div>
  );
}

// Paleta para distinguir los nodos comparados en tablas y gráficas.
const PAL_COMP = ["#2563eb", "#dc2626", "#059669", "#d97706", "#7c3aed",
  "#0891b2", "#be185d", "#4d7c0f", "#b45309", "#475569"];

/** Reporte comparativo: la misma información del detalle de un nodo (promedios
 *  MDA/MTR, perfil anual por hora y últimos días) pero con los nodos/zonas
 *  seleccionados superpuestos — puede mezclar PML y PND. */
function ComparacionModal({
  items,
  onQuitar,
  onClose,
}: {
  items: { modo: Modo; clave: string }[];
  onQuitar: (modo: Modo, clave: string) => void;
  onClose: () => void;
}) {
  const qs = useQueries({
    queries: items.map((it) => ({
      queryKey: ["mapa-detalle", it.modo, it.clave],
      queryFn: () => getMapaDetalle(it.modo, it.clave),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const dets = qs.map((q) => q.data).filter((d): d is MapaDetalle => d != null);
  const colorDe = (clave: string) =>
    PAL_COMP[Math.max(0, items.findIndex((it) => it.clave === clave)) % PAL_COMP.length];
  const claves = items.map((it) => it.clave);
  const nPml = items.filter((it) => it.modo === "pml").length;
  const nPnd = items.length - nPml;

  // Une las series de todos los nodos en filas por hora / por fecha-hora, con
  // una columna por clave (así cada nodo es una línea de la gráfica).
  const perfil = (mercado: Mercado) => {
    const filas = new Map<number, Record<string, number | null>>();
    for (const d of dets)
      for (const p of d.perfil_hora) {
        const f = filas.get(p.hora) ?? { hora: p.hora };
        f[d.clave] = p[mercado];
        filas.set(p.hora, f);
      }
    return [...filas.values()].sort((a, b) => (a.hora as number) - (b.hora as number));
  };
  const serie = (mercado: Mercado) => {
    const filas = new Map<string, Record<string, unknown>>();
    for (const d of dets)
      for (const p of d.serie7d) {
        const k = `${p.fecha}T${String(p.hora).padStart(2, "0")}`;
        const f = filas.get(k) ?? {
          k, x: `${p.fecha.slice(8, 10)}/${p.fecha.slice(5, 7)} h${p.hora}`,
        };
        f[d.clave] = p[mercado];
        filas.set(k, f);
      }
    return [...filas.values()].sort((a, b) => ((a.k as string) < (b.k as string) ? -1 : 1));
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-slate-900/40 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b flex items-center gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Comparación de {claves.length}
              {nPml > 0 && ` · ${nPml} nodo${nPml === 1 ? "" : "s"} (PML)`}
              {nPnd > 0 && ` · ${nPnd} zona${nPnd === 1 ? "" : "s"} (PND)`}
            </div>
            <div className="text-[11px] text-slate-400">$/MWh · MDA y MTR · mismo contenido que el detalle de cada nodo</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-sm text-slate-500 hover:text-slate-900 border rounded px-2.5 py-1"
          >
            Cerrar ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {qs.some((q) => q.isLoading) && (
            <div className="text-sm text-slate-500">Cargando detalle de los nodos…</div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="px-3 py-2 text-left">Nodo / Zona</th>
                  <th className="px-3 py-2 text-left">Sistema / zona</th>
                  <th className="px-3 py-2 text-right">MDA último día</th>
                  <th className="px-3 py-2 text-right">MDA prom. año</th>
                  <th className="px-3 py-2 text-right">MTR último día</th>
                  <th className="px-3 py-2 text-right">MTR prom. año</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {dets.map((d) => (
                  <tr key={d.clave} className="border-b last:border-0">
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle"
                        style={{ background: colorDe(d.clave) }} />
                      <span className="font-medium">{d.nombre}</span>
                      <span className="ml-1.5 font-mono text-xs text-slate-400">{d.clave}</span>
                      <span className={`ml-1.5 text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
                        d.tipo === "pml" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"
                      }`}>
                        {d.tipo.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">
                      {d.sistema}{d.tipo === "pml" ? ` · ${d.zona}` : ""}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {d.mercados.mda.ultimo?.precio != null ? `$${fmt.format(d.mercados.mda.ultimo.precio)}` : "—"}
                      <span className="block text-[10px] text-slate-400">
                        {d.mercados.mda.ultimo ? fmtFecha(d.mercados.mda.ultimo.fecha) : ""}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {d.mercados.mda.promedio_anio != null ? `$${fmt.format(d.mercados.mda.promedio_anio)}` : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {d.mercados.mtr.ultimo?.precio != null ? `$${fmt.format(d.mercados.mtr.ultimo.precio)}` : "—"}
                      <span className="block text-[10px] text-slate-400">
                        {d.mercados.mtr.ultimo ? fmtFecha(d.mercados.mtr.ultimo.fecha) : ""}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {d.mercados.mtr.promedio_anio != null ? `$${fmt.format(d.mercados.mtr.promedio_anio)}` : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => onQuitar(d.tipo, d.clave)}
                        className="text-slate-400 hover:text-red-600"
                        title="Quitar de la comparación"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <GraficaComp titulo={`Perfil por hora del año (MDA)`} data={perfil("mda")}
              claves={dets.map((d) => d.clave)} colorDe={colorDe} xKey="hora" xInterval={3} />
            <GraficaComp titulo={`Perfil por hora del año (MTR)`} data={perfil("mtr")}
              claves={dets.map((d) => d.clave)} colorDe={colorDe} xKey="hora" xInterval={3} />
            <GraficaComp titulo="Últimos días disponibles (MDA)" data={serie("mda")}
              claves={dets.map((d) => d.clave)} colorDe={colorDe} xKey="x" xInterval={23} />
            <GraficaComp titulo="Últimos días disponibles (MTR, con rezago)" data={serie("mtr")}
              claves={dets.map((d) => d.clave)} colorDe={colorDe} xKey="x" xInterval={23} />
          </div>

          <p className="text-[10px] text-slate-400">
            MDA = Mercado del Día en Adelanto · MTR = Tiempo Real (se publica con rezago).
            El promedio anual y las series son las mismas que muestra el detalle de cada nodo o zona.
          </p>
        </div>
      </div>
    </div>
  );
}

function GraficaComp({ titulo, data, claves, colorDe, xKey, xInterval }: {
  titulo: string; data: Record<string, unknown>[]; claves: string[];
  colorDe: (clave: string) => string; xKey: string; xInterval: number;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1">{titulo}</div>
      <div className="border rounded-lg p-2">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tick={{ fontSize: 9 }} interval={xInterval} />
            <YAxis tick={{ fontSize: 9 }} width={44}
              tickFormatter={(v: number) => fmt0.format(v)} domain={["auto", "auto"]} />
            <RTooltip
              formatter={(v: number, name: string) => [`$${fmt.format(v)}/MWh`, name]}
              labelFormatter={(l) => String(l)}
              contentStyle={{ fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {claves.map((c) => (
              <Line key={c} type="monotone" dataKey={c} name={c} stroke={colorDe(c)}
                dot={false} strokeWidth={1.6} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Marcador({ it, modo, color, sel, enComp, onClick }: {
  it: MapaMarcador; modo: Modo; color: string; sel: boolean; enComp: boolean; onClick: () => void;
}) {
  return (
    <CircleMarker
      center={[it.lat, it.lon]}
      radius={modo === "pml" ? (sel || enComp ? 7 : 4) : (sel || enComp ? 11 : 8)}
      pathOptions={{
        // Anillo azul = seleccionado o en comparación; blanco fino = el resto.
        color: sel || enComp ? "#1d4ed8" : "#ffffff", weight: sel ? 2.5 : enComp ? 2 : 0.8,
        fillColor: color, fillOpacity: 0.85,
      }}
      eventHandlers={{ click: onClick }}
    >
      <LTooltip direction="top" offset={[0, -4]}>
        <span className="text-xs">
          <b>{it.nombre}</b> ({it.clave})<br />
          {it.precio != null ? `$${fmt.format(it.precio)}/MWh` : "sin precio"}
          {modo === "pnd" && it.n_nodos != null ? ` · ${it.n_nodos} nodos` : ""}
          {it.ubicacion && (
            <>
              <br />
              <span className={UBICACION[it.ubicacion].aprox ? "text-amber-700" : "text-slate-500"}>
                {UBICACION[it.ubicacion].aprox ? "≈ " : ""}
                {ubicacionTexto(it.ubicacion, it.ubicacion_nombre)}
              </span>
            </>
          )}
        </span>
      </LTooltip>
    </CircleMarker>
  );
}

function nombreSub(red: MapaConectividad, id: string): string {
  return red.subestaciones.find((s) => s.id === id)?.nombre ?? id;
}

/** Encaja el mapa a la red dibujada al cambiar el nodo o los saltos (se monta
 *  solo cuando ya hay red, así el efecto corre con los bounds listos). */
function AjustarVista({ bounds, clave }: { bounds: LatLngBoundsExpression | null; clave: string }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  return null;
}

/** Modo Operación: la red 400/230 kV alcanzada desde el nodo elegido, por
 *  saltos, con nuestros nodos en cada subestación, su precio (promedio del
 *  último día del mercado elegido) y la diferencia frente al nodo elegido. */
function PanelConectividad({ q, saltos, onSaltos, sel, precioDe, mercado, onIr }: {
  q: UseQueryResult<MapaConectividad, Error>;
  saltos: number;
  onSaltos: (n: number) => void;
  sel: string;
  precioDe: Map<string, number | null>;
  mercado: Mercado;
  onIr: (clave: string) => void;
}) {
  const d = q.data;
  const base = precioDe.get(sel) ?? null;
  const porSalto = new Map<number, MapaConectSub[]>();
  for (const s of d?.subestaciones ?? []) porSalto.set(s.salto, [...(porSalto.get(s.salto) ?? []), s]);
  const nombreDe = (id: string | null) =>
    (id && d?.subestaciones.find((s) => s.id === id)?.nombre) || null;
  const nSubs = Math.max(0, (d?.subestaciones.length ?? 0) - 1);
  const nNodos = (d?.subestaciones ?? []).reduce((acc, s) => acc + s.nodos.length, 0);

  return (
    <div className="border-b bg-amber-50/60">
      <div className="px-4 py-2.5 flex items-center gap-2">
        <Network className="w-4 h-4 text-amber-600" />
        <div className="text-xs font-semibold text-slate-800">Modo Operación · red de transmisión</div>
        <div className="ml-auto flex items-center gap-1 text-[11px] text-slate-500" title="Cuántas subestaciones de distancia recorrer">
          saltos
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onSaltos(n)}
              aria-pressed={saltos === n}
              className={`w-6 h-6 rounded border text-xs ${
                saltos === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      {q.isLoading && <div className="px-4 pb-3 text-xs text-slate-500">Recorriendo la red…</div>}
      {q.isError && <div className="px-4 pb-3 text-xs text-red-600">{q.error.message}</div>}
      {d && !d.subestacion && <div className="px-4 pb-3 text-xs text-amber-800">{d.motivo}</div>}
      {d?.subestacion && (
        <div className="px-4 pb-3 space-y-2.5">
          <div className="text-[11px] text-slate-500">
            {nSubs} {plural(nSubs, "subestación alcanzada", "subestaciones alcanzadas")} en {saltos}{" "}
            {plural(saltos, "salto", "saltos")} · {nNodos} {plural(nNodos, "nodo", "nodos")} · precio = promedio del
            último día {mercado.toUpperCase()} · Δ frente a <span className="font-mono">{sel}</span>
          </div>
          {[...porSalto.keys()].sort((a, b) => a - b).map((salto) => (
            <div key={salto}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {salto === 0 ? "Subestación del nodo" : `Salto ${salto}`}
              </div>
              <div className="space-y-1.5">
                {porSalto.get(salto)!.map((s) => (
                  <div key={s.id} className="border rounded-md bg-white px-2.5 py-1.5">
                    <div className="flex items-baseline gap-1.5 text-xs">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full border-2 shrink-0"
                        style={{ borderColor: salto === 0 ? COLOR_SUB_ORIGEN : "#0f172a" }}
                      />
                      <span className="font-medium text-slate-800">{s.nombre ?? "Subestación sin nombre"}</span>
                      {s.kv.length > 0 && (
                        <span className="text-slate-400">{s.kv.map((k) => `${k} kV`).join("/")}</span>
                      )}
                    </div>
                    {salto > 0 && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        ~{fmt0.format(s.km ?? 0)} km {s.via ? `por ${s.via}` : "por línea sin nombre en OSM"}
                        {nombreDe(s.desde) ? ` desde ${nombreDe(s.desde)}` : ""}
                      </div>
                    )}
                    {s.nodos.length === 0 ? (
                      <div className="text-[11px] text-slate-400 mt-1">
                        Sin nodos de precios anclados a esta subestación.
                      </div>
                    ) : (
                      <table className="w-full text-[11px] mt-1">
                        <tbody>
                          {s.nodos.map((n) => {
                            const p = precioDe.get(n.clave) ?? null;
                            const delta = p != null && base != null ? p - base : null;
                            const esSel = n.clave === sel;
                            return (
                              <tr
                                key={n.clave}
                                className={esSel ? "bg-blue-50" : "hover:bg-slate-50 cursor-pointer"}
                                onClick={() => !esSel && onIr(n.clave)}
                                title={esSel ? "Nodo elegido" : "Ver este nodo y recorrer la red desde él"}
                              >
                                <td className="py-0.5 pr-2 font-mono text-slate-500 whitespace-nowrap">{n.clave}</td>
                                <td className="py-0.5 pr-2 text-slate-700">
                                  {n.nombre}
                                  {n.tension_kv != null && (
                                    <span className="text-slate-400"> · {fmt0.format(n.tension_kv)} kV</span>
                                  )}
                                </td>
                                <td className="py-0.5 text-right tabular-nums whitespace-nowrap">
                                  {p != null ? `$${fmt.format(p)}` : "—"}
                                </td>
                                <td
                                  className={`py-0.5 pl-2 text-right tabular-nums whitespace-nowrap ${
                                    esSel || delta == null
                                      ? "text-slate-300"
                                      : delta > 0 ? "text-red-700" : delta < 0 ? "text-green-700" : "text-slate-500"
                                  }`}
                                >
                                  {esSel ? "base" : delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt.format(delta)}`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[10px] text-slate-400">
            Red de OpenStreetMap{d.generado ? ` (extracto del ${fmtFecha(d.generado.slice(0, 10))})` : ""}: líneas
            de 69 a 400 kV, con la cobertura que tenga OSM en cada zona; los tramos punteados siguen hacia
            una subestación que OSM no tiene mapeada. Clic en un nodo de la lista para recorrer la red desde él.
          </p>
        </div>
      )}
    </div>
  );
}

function Grafica({ titulo, data, xKey, xInterval }: {
  titulo: string; data: Record<string, unknown>[]; xKey: string; xInterval: number;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1">{titulo}</div>
      <div className="border rounded-lg p-2">
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tick={{ fontSize: 9 }} interval={xInterval} />
            <YAxis tick={{ fontSize: 9 }} width={44}
              tickFormatter={(v: number) => fmt0.format(v)} domain={["auto", "auto"]} />
            <RTooltip
              formatter={(v: number, name: string) => [`$${fmt.format(v)}/MWh`, name.toUpperCase()]}
              labelFormatter={(l) => String(l)}
              contentStyle={{ fontSize: 11 }}
            />
            <Line type="monotone" dataKey="mda" name="mda" stroke="#2563eb" dot={false} strokeWidth={1.6} connectNulls />
            <Line type="monotone" dataKey="mtr" name="mtr" stroke="#dc2626" dot={false} strokeWidth={1.4} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
